import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, ShieldCheck, FileText, Sparkles, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import StaircaseViz from "../components/StaircaseViz";
import { useLang } from "../lib/LangContext";

export default function LandingPage() {
  const { t } = useLang();

  return (
    <div>
      <SEO page="home" canonicalPath="/" />
      <Header variant="landing" />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 motif-dots pointer-events-none" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] px-3 py-1.5 rounded-full border border-slate-900/10 bg-white/80" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#DE0F72" }} />
              {t("hero.badge")}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-slate-900 reveal" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl reveal reveal-delay-1" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 reveal reveal-delay-2">
              <Link to="/diagnostic" className="btn-primary" data-testid="hero-cta-primary">
                {t("hero.cta_primary")} <ArrowRight size={18} />
              </Link>
              <a href="#methode" className="btn-secondary" data-testid="hero-cta-secondary">
                {t("hero.cta_secondary")}
              </a>
            </div>

            <div className="mt-6 text-sm text-slate-500 reveal reveal-delay-3">{t("hero.resume_hint")}</div>

            <ul className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 reveal reveal-delay-4">
              <TrustPill icon={<Clock size={14} />} label={t("hero.trust.time")} />
              <TrustPill icon={<ShieldCheck size={14} />} label={t("hero.trust.confidential")} />
              <TrustPill icon={<FileText size={14} />} label={t("hero.trust.pdf")} />
              <TrustPill icon={<Sparkles size={14} />} label={t("hero.trust.no_commit")} />
            </ul>
          </div>

          <div className="lg:col-span-5 reveal reveal-delay-2">
            <div className="card-ariovis relative overflow-hidden">
              <div className="absolute inset-0 rounded-[14px] motif-hatch pointer-events-none" aria-hidden />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="overline">{t("method.overline")}</div>
                    <div className="mt-1 font-bold text-slate-900 text-base md:text-lg leading-tight">
                      {t("method.title")}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-1 text-[10px] font-bold tracking-widest text-slate-500">
                    <span className="w-2 h-2 rounded-full" style={{ background: "#DE0F72" }} />
                    5 {t("method.overline") === "MÉTHODE" ? "PALIERS" : "TIERS"}
                  </div>
                </div>
                <div className="mt-5">
                  <StaircaseViz current={0} target={4} variant="ladder" />
                </div>
                <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                  {t("method.subtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-t border-slate-200 bg-white" data-testid="section-problem">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">{t("problem.overline")}</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="problem-title">
              {t("problem.title")}
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-4">
              {["problem.p1", "problem.p2", "problem.p3", "problem.p4"].map((k, i) => (
                <li key={k} className="flex gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ["#9E0E40", "#FF9900", "#B58900", "#8A0E9E"][i] }} />
                  <span className="text-slate-700">{t(k)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-slate-900 font-medium border-l-2 border-slate-900 pl-4">
              {t("problem.conclusion")}
            </p>
          </div>
        </div>
      </section>

      {/* MÉTHODE */}
      <section id="methode" className="relative border-t border-slate-200 bg-slate-900 text-slate-100 overflow-hidden" data-testid="section-method">
        <div className="absolute inset-0 motif-circuit opacity-[0.08]" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#DE0F72" }}>{t("method.overline")}</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-white">{t("method.title")}</h2>
            <p className="mt-4 text-slate-300">{t("method.subtitle")}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((lvl) => (
              <div key={lvl} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:border-white/25 transition-colors" data-testid={`method-level-${lvl}`}>
                <div className="text-4xl font-bold" style={{ color: ["#9E0E40", "#FF9900", "#B58900", "#FF76B7", "#8A0E9E"][lvl - 1] }}>
                  {lvl}
                </div>
                <div className="mt-3 text-xs uppercase tracking-widest text-slate-400">
                  {t(`method.level.${lvl}.short`)}
                </div>
                <div className="mt-1 text-white font-bold">{t(`method.level.${lvl}.title`)}</div>
                <div className="mt-2 text-sm text-slate-300 leading-relaxed">{t(`method.level.${lvl}.desc`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVRABLE */}
      <section id="livrable" className="border-t border-slate-200 bg-white" data-testid="section-deliverable">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">{t("deliverable.overline")}</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{t("deliverable.title")}</h2>
            <ul className="mt-8 space-y-3">
              {[
                "deliverable.item.level", "deliverable.item.radar", "deliverable.item.forces",
                "deliverable.item.gaps", "deliverable.item.wins", "deliverable.item.roadmap",
                "deliverable.item.matrix", "deliverable.item.offer", "deliverable.item.pdf",
              ].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <Check size={18} className="mt-0.5 text-slate-900 flex-shrink-0" />
                  <span className="text-slate-700">{t(k)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="card-ariovis a4-preview overflow-hidden relative">
              <div className="absolute inset-0 motif-dots opacity-30 pointer-events-none" aria-hidden />
              <div className="relative p-8">
                <div className="flex items-center justify-between text-[10px] tracking-widest text-slate-500">
                  <span>ARIOVIS</span>
                  <span>ARV-XXXXXXXX</span>
                </div>
                <div className="mt-6 text-xs font-bold uppercase tracking-widest" style={{ color: "#DE0F72" }}>ARIOVIS</div>
                <div className="mt-2 text-2xl font-bold text-slate-900">Roadmap IAM alignée Zero Trust</div>
                <div className="mt-1 text-sm text-slate-500">Étude indicative personnalisée</div>
                <div className="mt-8 h-[2px] w-1/3" style={{ background: "#DE0F72" }} />
                <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="text-slate-500">Société</div><div className="text-slate-900 font-semibold">—</div>
                  <div className="text-slate-500">Destinataire</div><div className="text-slate-900 font-semibold">—</div>
                  <div className="text-slate-500">Date</div><div className="text-slate-900 font-semibold">—</div>
                </div>
                <div className="mt-8">
                  <StaircaseViz current={0} target={3} variant="steps" height={130} showLabels={false} />
                </div>
                <div className="mt-6 space-y-2">
                  <div className="h-3 w-2/3 bg-slate-200 rounded-full"></div>
                  <div className="h-3 w-4/5 bg-slate-200 rounded-full"></div>
                  <div className="h-3 w-1/2 bg-slate-200 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="border-t border-slate-200 bg-slate-50" data-testid="section-how">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <div className="overline">{t("how.overline")}</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{t("how.title")}</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="card-ariovis" data-testid={`how-step-${n}`}>
                <div className="text-5xl font-bold text-slate-200">0{n}</div>
                <div className="mt-4 font-bold text-slate-900">{t(`how.step${n}.title`)}</div>
                <div className="mt-2 text-sm text-slate-600">{t(`how.step${n}.desc`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-slate-200 bg-white" data-testid="section-faq">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="overline">{t("faq.overline")}</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{t("faq.title")}</h2>
          <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <details key={n} className="group py-5" data-testid={`faq-item-${n}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-semibold text-slate-900">{t(`faq.q${n}`)}</span>
                  <span className="mt-1 w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{t(`faq.a${n}`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-slate-200 bg-slate-900 text-white" data-testid="section-final-cta">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t("cta.final.title")}</h2>
          <p className="mt-4 text-slate-300">{t("cta.final.subtitle")}</p>
          <div className="mt-8">
            <Link to="/diagnostic" className="btn-primary" style={{ background: "#DE0F72", borderColor: "#DE0F72" }} data-testid="final-cta-start">
              {t("hero.cta_primary")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustPill({ icon, label }) {
  return (
    <li className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
      <span className="text-slate-500">{icon}</span>
      <span>{label}</span>
    </li>
  );
}
